import { cn } from '@/lib/cn';
import type { Artifact, ArtifactVersion } from '@innermate/shared';
import {
  Activity,
  AlertTriangle,
  BookmarkPlus,
  Check,
  ChevronLeft,
  ChevronRight,
  Code2,
  Eye,
  Loader2,
  Maximize2,
  Minimize2,
  Share2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { CodeArtifact } from './CodeArtifact';
import { HtmlArtifact } from './HtmlArtifact';
import { MarkdownArtifact } from './MarkdownArtifact';
import { MermaidArtifact } from './MermaidArtifact';
import { ReactArtifact } from './ReactArtifact';
import { SvgArtifact } from './SvgArtifact';
import type { ArtifactRpcFn } from './useArtifactBridge';

interface Props {
  artifact: Artifact;
  /** effectiveVersion 의 본문. 로딩 중이거나 미선택이면 null. */
  versionContent: ArtifactVersion | null;
  effectiveVersion: number | null;
  isLoading: boolean;
  onClose: () => void;
  /** 헤더의 ◀▶ 네비가 호출. null 이면 currentVersion 으로 자동 정규화. */
  onSelectVersion: (version: number | null) => void;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  /** 공유 링크 복사. 미지정 시 공유 버튼 미렌더 (관리자 검토 패널 — 보기 전용). */
  onShare?: () => void;
  /** 공유 버튼 피드백: 클릭 직후 '복사됨' → 2 초 후 'idle'. */
  shareFeedback?: 'idle' | 'copied' | 'error';
  /**
   * dashboard.create 가 만든 artifact 본문에는 첫 줄 marker (`<!--innermate-dashboard ...-->`) 가
   * 박혀있다. 그 경우 "저장" 버튼이 헤더에 노출되고, 클릭하면 이 핸들러가 호출되어
   * DashboardSaveDialog 를 띄운다. 핸들러가 undefined 면 버튼 미렌더.
   */
  onSaveDashboard?: (artifactId: string, defaultTitle: string) => void;
  /** dashboard artifact 에서 분석 버튼 (Activity) 클릭. */
  onAnalyzeDashboard?: (artifactId: string, content: string) => void;
  /** iframe RPC 라우팅 오버라이드 (관리자 검토 패널 — 세션 소유자 스코프). 미지정 시 사용자 경로. */
  rpc?: ArtifactRpcFn;
}

/**
 * 우측 캔버스 패널. 헤더 (kind chip + 제목 + 버전 네비 + 코드보기 토글 + 닫기) + 본문.
 */
export function ArtifactPanel({
  artifact,
  versionContent,
  effectiveVersion,
  isLoading,
  onClose,
  onSelectVersion,
  isFullscreen,
  onToggleFullscreen,
  onShare,
  shareFeedback,
  onSaveDashboard,
  onAnalyzeDashboard,
  rpc,
}: Props) {
  const [viewMode, setViewMode] = useState<'render' | 'code'>('render');
  /** 런타임에 rpc 가 query_expired 로 응답한 queryId 집합. 정규식으로 못 잡힌 동적 호출 보강. */
  const [runtimeExpired, setRuntimeExpired] = useState<Set<string>>(() => new Set());

  // artifact 가 바뀌면 뷰와 런타임 만료 집합 모두 초기화.
  // biome-ignore lint/correctness/useExhaustiveDependencies: id 변경 시 reset 의도.
  useEffect(() => {
    setViewMode('render');
    setRuntimeExpired(new Set());
  }, [artifact.id]);

  // 버전 전환 시에도 런타임 만료 집합 초기화 — 이전 버전의 실패를 새 버전에 끌고 오면 안 됨.
  // biome-ignore lint/correctness/useExhaustiveDependencies: effectiveVersion 이 바뀔 때마다 trigger 가 핵심 의도
  useEffect(() => {
    setRuntimeExpired(new Set());
  }, [effectiveVersion]);

  const handleQueryExpired = useCallback((queryId: string | null) => {
    setRuntimeExpired((prev) => {
      const next = new Set(prev);
      next.add(queryId ?? '__unknown__');
      return next;
    });
  }, []);

  const staticExpiredCount = versionContent?.expiredQueryIds?.length ?? 0;
  const isExpired = staticExpiredCount > 0 || runtimeExpired.size > 0;

  // 본문 첫 줄의 marker 로 dashboard 식별. dashboard.create 가 박는 형식:
  // `<!--innermate-dashboard direction="..." intent="..."-->`. 옛 marker 도 호환.
  const isDashboard =
    artifact.kind === 'html' &&
    !!versionContent?.content?.trimStart().startsWith('<!--innermate-dashboard');

  const total = artifact.currentVersion;
  const v = effectiveVersion ?? total;
  const canPrev = v > 1;
  const canNext = v < total;
  const isLatest = v === total;

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-surface-muted">
      <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border bg-surface px-3">
        <span
          className={cn(
            'inline-flex h-5 shrink-0 items-center rounded px-1.5 text-[10px] uppercase tracking-wide',
            'bg-surface-muted font-mono text-foreground-muted',
          )}
        >
          {artifact.kind}
        </span>
        <h2
          className="min-w-0 flex-1 truncate text-sm font-medium text-foreground"
          title={artifact.title}
        >
          {artifact.title}
        </h2>

        {/* 버전 네비 */}
        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            aria-label="이전 버전"
            disabled={!canPrev}
            onClick={() => onSelectVersion(v - 1)}
            className="grid size-7 place-items-center rounded-md text-foreground-muted enabled:hover:bg-surface-muted enabled:hover:text-foreground disabled:opacity-30"
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            onClick={() => onSelectVersion(null)}
            title="최신 버전으로"
            className={cn(
              'h-7 rounded-md px-1.5 text-xs tabular-nums',
              isLatest ? 'text-foreground-muted' : 'text-foreground hover:bg-surface-muted',
            )}
          >
            v{v}
            {total > 1 ? <span className="text-foreground-muted">/{total}</span> : null}
          </button>
          <button
            type="button"
            aria-label="다음 버전"
            disabled={!canNext}
            onClick={() => onSelectVersion(v + 1)}
            className="grid size-7 place-items-center rounded-md text-foreground-muted enabled:hover:bg-surface-muted enabled:hover:text-foreground disabled:opacity-30"
          >
            <ChevronRight className="size-4" />
          </button>
        </div>

        {/* dashboard 저장 — chat artifact 의 currentVersion spec 을 dashboards 1 row 로 복사 */}
        {isDashboard && onSaveDashboard ? (
          <button
            type="button"
            aria-label="대시보드 저장"
            onClick={() => onSaveDashboard(artifact.id, artifact.title)}
            className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
            title="대시보드 저장 — 좌측 사이드바 목록에 추가"
          >
            <BookmarkPlus className="size-4" />
          </button>
        ) : null}

        {/* dashboard 분석 — saved_query 결과로 컬럼 프로파일 + 데이터 품질 A~D */}
        {isDashboard && onAnalyzeDashboard && versionContent ? (
          <button
            type="button"
            aria-label="분석"
            onClick={() => onAnalyzeDashboard(artifact.id, versionContent.content)}
            className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
            title="분석 — 컬럼 프로파일 / 데이터 품질"
          >
            <Activity className="size-4" />
          </button>
        ) : null}

        {/* 코드보기 토글 */}
        <button
          type="button"
          aria-label={viewMode === 'render' ? '코드 보기' : '렌더 보기'}
          onClick={() => setViewMode((m) => (m === 'render' ? 'code' : 'render'))}
          className={cn(
            'grid size-7 place-items-center rounded-md',
            viewMode === 'code'
              ? 'bg-surface-muted text-foreground'
              : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
          )}
          title={viewMode === 'render' ? '코드 보기' : '렌더 보기'}
        >
          {viewMode === 'render' ? <Code2 className="size-4" /> : <Eye className="size-4" />}
        </button>

        {onShare ? (
          <button
            type="button"
            aria-label="공유 링크 복사"
            onClick={onShare}
            className={cn(
              'grid size-7 place-items-center rounded-md',
              shareFeedback === 'copied'
                ? 'bg-surface-muted text-foreground'
                : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
            )}
            title={
              shareFeedback === 'copied'
                ? '링크가 복사됐습니다'
                : shareFeedback === 'error'
                  ? '복사 실패 — 브라우저 권한 확인'
                  : '공유 링크 복사'
            }
          >
            {shareFeedback === 'copied' ? (
              <Check className="size-4" />
            ) : (
              <Share2 className="size-4" />
            )}
          </button>
        ) : null}

        <button
          type="button"
          aria-label={isFullscreen ? '전체화면 해제' : '전체화면'}
          onClick={onToggleFullscreen}
          className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
          title={isFullscreen ? '전체화면 해제 (Esc)' : '전체화면'}
        >
          {isFullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
        </button>

        <button
          type="button"
          aria-label="패널 닫기"
          onClick={onClose}
          className="grid size-7 place-items-center rounded-md text-foreground-muted hover:bg-surface-muted hover:text-foreground"
        >
          <X className="size-4" />
        </button>
      </header>

      {/* changeNote */}
      {versionContent?.changeNote ? (
        <div className="shrink-0 border-b border-border bg-surface px-3 py-1.5 text-xs text-foreground-muted">
          v{v} 변경: {versionContent.changeNote}
        </div>
      ) : null}

      <div className="relative flex-1 min-h-0 overflow-hidden bg-surface">
        {isLoading && !versionContent ? (
          <div className="flex h-full items-center justify-center text-sm text-foreground-muted">
            <Loader2 className="mr-2 size-4 animate-spin" />
            본문 불러오는 중…
          </div>
        ) : !versionContent ? (
          <div className="flex h-full items-center justify-center text-sm text-foreground-muted">
            본문이 없습니다.
          </div>
        ) : versionContent.content.trim().length === 0 ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-foreground-muted">
            이 버전의 본문이 비어 있습니다. LLM 에게 다시 생성해 달라고 요청하세요.
          </div>
        ) : viewMode === 'code' ? (
          <CodeArtifact source={versionContent.content} />
        ) : (
          <ArtifactBody
            kind={artifact.kind}
            content={versionContent.content}
            artifactId={artifact.id}
            onQueryExpired={handleQueryExpired}
            rpc={rpc}
            dashboardVersion={versionContent.version}
          />
        )}
        {isExpired && viewMode === 'render' ? (
          <ExpiredOverlay staticCount={staticExpiredCount} runtimeCount={runtimeExpired.size} />
        ) : null}
      </div>
    </div>
  );
}

function ArtifactBody({
  kind,
  content,
  artifactId,
  onQueryExpired,
  rpc,
  dashboardVersion,
}: {
  kind: Artifact['kind'];
  content: string;
  artifactId: string;
  onQueryExpired: (queryId: string | null) => void;
  rpc?: ArtifactRpcFn;
  dashboardVersion?: number;
}) {
  switch (kind) {
    case 'html':
      return (
        <HtmlArtifact
          html={content}
          artifactId={artifactId}
          onQueryExpired={onQueryExpired}
          rpc={rpc}
          dashboardVersion={dashboardVersion}
        />
      );
    case 'react':
      return <ReactArtifact jsx={content} />;
    case 'svg':
      return <SvgArtifact svg={content} />;
    case 'mermaid':
      return <MermaidArtifact source={content} />;
    case 'markdown':
      return <MarkdownArtifact source={content} />;
    case 'code':
      return <CodeArtifact source={content} />;
  }
}

function ExpiredOverlay({
  staticCount,
  runtimeCount,
}: {
  staticCount: number;
  runtimeCount: number;
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: <output> 태그는 form 컨텍스트 의미가 강해 일반 status 표시에는 div 가 적합
    <div
      role="status"
      aria-live="polite"
      className="absolute inset-0 flex items-center justify-center bg-surface/90 backdrop-blur-sm p-6 text-center"
    >
      <div className="max-w-md space-y-3">
        <div className="mx-auto inline-flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="size-6" />
        </div>
        <h3 className="text-base font-semibold">대시보드가 만료되었습니다</h3>
        <p className="text-sm text-foreground-muted">
          이 대시보드가 사용하는 저장된 쿼리 중 일부가 관리자에 의해 제거되었거나 원본 대화가
          삭제되었습니다. 새로운 대화에서 다시 만들어 주세요.
        </p>
        {staticCount + runtimeCount > 0 ? (
          <p className="text-xs text-foreground-muted font-mono">
            만료된 쿼리 {staticCount + runtimeCount}건 감지
          </p>
        ) : null}
      </div>
    </div>
  );
}
